import React, { useState } from "react";
import { Users, Minus, Plus, BedDouble } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

/**
 * Booking.com-style guest selector: adults / children (2-9) / infants (0-2) + optional rooms.
 * - Infants: no bed, no meal (free)
 * - Children: no meal charge, DO count for bed capacity
 * Props:
 *  - value: { adults, children, infants, rooms?, breakdown? }
 *  - onChange: ({ adults, children, infants, rooms?, breakdown? }) => void
 *  - showRooms: boolean — adds a "Oda" (rooms) stepper row (typed input, up to 99)
 *  - showBreakdown: boolean — adds mandatory 2/3/4-person room distribution steppers
 *  - variant: "light" | "dark"
 *  - lang: "tr" | "en"
 *  - testId?: string prefix (default "guest-selector")
 */
const LIMITS = { adults: [1, 200], children: [0, 100], infants: [0, 100], rooms: [1, 99] };
const BD_DEFS = [
    { key: "double", cap: 2 },
    { key: "triple", cap: 3 },
    { key: "quad", cap: 4 },
];

const syncBreakdown = (bd, roomsTotal) => {
    const next = { double: bd.double || 0, triple: bd.triple || 0, quad: bd.quad || 0 };
    let delta = roomsTotal - (next.double + next.triple + next.quad);
    if (delta > 0) next.double += delta;
    else if (delta < 0) {
        let need = -delta;
        for (const k of ["double", "triple", "quad"]) {
            const take = Math.min(next[k], need);
            next[k] -= take;
            need -= take;
            if (!need) break;
        }
    }
    return next;
};

const GuestSelector = ({ value, onChange, variant = "light", lang = "tr", testId = "guest-selector", showRooms = false, showBreakdown = false }) => {
    const [open, setOpen] = useState(false);
    const rooms = value?.rooms ?? 1;
    const v = {
        adults: value?.adults ?? 2,
        children: value?.children ?? 0,
        infants: value?.infants ?? 0,
        ...(showRooms ? { rooms } : {}),
        ...(showBreakdown ? { breakdown: value?.breakdown ?? { double: rooms, triple: 0, quad: 0 } } : {}),
    };
    const isDark = variant === "dark";
    const en = lang === "en";

    const bd = v.breakdown || { double: 0, triple: 0, quad: 0 };
    const bdSum = (bd.double || 0) + (bd.triple || 0) + (bd.quad || 0);
    const bdDiff = showBreakdown ? (v.rooms ?? 1) - bdSum : 0;

    const rows = [
        { key: "adults", label: en ? "Adults" : "Yetişkin", sub: en ? "10 years and older" : "10 yaş ve üzeri" },
        { key: "children", label: en ? "Children" : "Çocuk", sub: en ? "2–9 years old" : "2–9 yaş arası" },
        { key: "infants", label: en ? "Infants" : "Bebek", sub: en ? "Under 2 years" : "2 yaş altı" },
        ...(showRooms ? [{ key: "rooms", label: en ? "Rooms" : "Oda", sub: en ? "Type or use +/−" : "Yazarak veya +/− ile" }] : []),
    ];

    const bdRows = BD_DEFS.map(({ key, cap }) => ({
        key,
        cap,
        label: en ? `${cap === 2 ? "Double" : cap === 3 ? "Triple" : "Quad"} Room` : `${cap} Kişilik Oda`,
        sub: en ? `${cap} beds` : `${cap} yatak`,
    }));

    const setRooms = (nextRooms) => {
        const patch = { ...v, rooms: nextRooms };
        if (showBreakdown) patch.breakdown = syncBreakdown(bd, nextRooms);
        onChange?.(patch);
    };

    const step = (key, delta) => {
        const [min, max] = LIMITS[key];
        const next = Math.min(max, Math.max(min, v[key] + delta));
        if (key === "rooms") setRooms(next);
        else onChange?.({ ...v, [key]: next });
    };

    const setBd = (key, raw) => {
        const n = Math.min(99, Math.max(0, Number.isNaN(raw) ? 0 : raw));
        onChange?.({ ...v, breakdown: { ...bd, [key]: n } });
    };

    const summaryParts = [
        `${v.adults} ${en ? (v.adults > 1 ? "adults" : "adult") : "yetişkin"}`,
        v.children > 0 && `${v.children} ${en ? (v.children > 1 ? "children" : "child") : "çocuk"}`,
        v.infants > 0 && `${v.infants} ${en ? (v.infants > 1 ? "infants" : "infant") : "bebek"}`,
        showRooms && `${v.rooms} ${en ? (v.rooms > 1 ? "rooms" : "room") : "oda"}`,
        showBreakdown && bdSum > 0 && `(${BD_DEFS.filter(({ key }) => bd[key] > 0).map(({ key, cap }) => `${bd[key]}×${cap}${en ? "p" : "'li"}`).join(" · ")})`,
    ].filter(Boolean);

    const numInput = (val, onChangeNum, tid, min = 0, max = 99) => (
        <input
            type="number"
            min={min}
            max={max}
            value={val}
            onFocus={(e) => e.target.select()}
            onChange={(e) => onChangeNum(parseInt(e.target.value, 10))}
            className="w-14 text-center font-bold font-mono border border-emerald_deep/25 py-1.5 outline-none focus:border-[var(--c-gold)] bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            data-testid={tid}
        />
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={
                        isDark
                            ? "input-dark w-full text-left cursor-pointer truncate"
                            : "w-full h-full text-left p-4 cursor-pointer hover:bg-emerald_deep/5 transition-colors"
                    }
                    data-testid={`${testId}-trigger`}
                >
                    {!isDark && (
                        <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-950/70 mb-1 font-bold flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" style={{ color: "var(--c-gold)" }} /> {en ? "Guests" : "Misafir"}
                        </div>
                    )}
                    <span className={isDark ? "text-sm" : "text-base font-semibold text-emerald-950"} data-testid={`${testId}-summary`}>
                        {summaryParts.join(" · ")}
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                side="bottom"
                sideOffset={8}
                collisionPadding={16}
                className="w-[320px] p-0 border-0 shadow-2xl bg-white text-emerald-950 rounded-none max-h-[70vh] overflow-y-auto"
                data-testid={`${testId}-popover`}
            >
                <div className="px-5 py-3 border-b border-emerald_deep/10">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald_deep/70 flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" style={{ color: "var(--c-gold)" }} /> {en ? "Guests" : "Misafirler"}
                    </div>
                </div>
                <div className="p-5 space-y-4">
                    {rows.map((r) => {
                        const [min, max] = LIMITS[r.key];
                        return (
                            <div key={r.key} className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm font-semibold">{r.label}</div>
                                    <div className="text-[11px] text-emerald_deep/60">{r.sub}</div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => step(r.key, -1)}
                                        disabled={v[r.key] <= min}
                                        className="w-8 h-8 border border-emerald_deep/25 flex items-center justify-center hover:border-[var(--c-gold)] hover:text-[var(--c-gold)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        data-testid={`${testId}-${r.key}-minus`}
                                        aria-label={`${r.label} azalt`}
                                    >
                                        <Minus className="w-3.5 h-3.5" />
                                    </button>
                                    {r.key === "rooms" ? (
                                        numInput(v.rooms, (raw) => setRooms(Number.isNaN(raw) ? min : Math.min(max, Math.max(min, raw))), `${testId}-rooms-count`, min, max)
                                    ) : (
                                        <span className="w-6 text-center font-bold font-mono" data-testid={`${testId}-${r.key}-count`}>{v[r.key]}</span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => step(r.key, 1)}
                                        disabled={v[r.key] >= max}
                                        className="w-8 h-8 border border-emerald_deep/25 flex items-center justify-center hover:border-[var(--c-gold)] hover:text-[var(--c-gold)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        data-testid={`${testId}-${r.key}-plus`}
                                        aria-label={`${r.label} artır`}
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}

                    {showBreakdown && (
                        <div className="border-t border-emerald_deep/10 pt-4 space-y-3" data-testid={`${testId}-breakdown`}>
                            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald_deep/70 flex items-center gap-1.5">
                                <BedDouble className="w-3.5 h-3.5" style={{ color: "var(--c-gold)" }} /> {en ? "Room Distribution" : "Oda Dağılımı"}
                            </div>
                            {bdRows.map((r) => (
                                <div key={r.key} className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="text-sm font-semibold">{r.label}</div>
                                        <div className="text-[11px] text-emerald_deep/60">{r.sub}</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setBd(r.key, (bd[r.key] || 0) - 1)}
                                            disabled={(bd[r.key] || 0) <= 0}
                                            className="w-8 h-8 border border-emerald_deep/25 flex items-center justify-center hover:border-[var(--c-gold)] hover:text-[var(--c-gold)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                            data-testid={`${testId}-bd-${r.key}-minus`}
                                            aria-label={`${r.label} azalt`}
                                        >
                                            <Minus className="w-3.5 h-3.5" />
                                        </button>
                                        {numInput(bd[r.key] || 0, (raw) => setBd(r.key, raw), `${testId}-bd-${r.key}-count`)}
                                        <button
                                            type="button"
                                            onClick={() => setBd(r.key, (bd[r.key] || 0) + 1)}
                                            disabled={(bd[r.key] || 0) >= 99}
                                            className="w-8 h-8 border border-emerald_deep/25 flex items-center justify-center hover:border-[var(--c-gold)] hover:text-[var(--c-gold)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                            data-testid={`${testId}-bd-${r.key}-plus`}
                                            aria-label={`${r.label} artır`}
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <div
                                className={`text-[11px] font-semibold px-3 py-2 border ${
                                    bdDiff === 0 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-300"
                                }`}
                                data-testid={`${testId}-bd-status`}
                            >
                                {bdDiff === 0
                                    ? (en ? "✓ Distribution complete" : "✓ Dağılım tamam")
                                    : bdDiff > 0
                                        ? (en ? `${bdDiff} more room(s) to distribute` : `${bdDiff} oda daha dağıtılmalı`)
                                        : (en ? `Distribution exceeds room count by ${-bdDiff}` : `Dağılım oda sayısını ${-bdDiff} aşıyor`)}
                            </div>
                        </div>
                    )}

                    <div className="border-t border-emerald_deep/10 pt-3 space-y-1.5">
                        <div className="text-[11px] text-emerald_deep/60 leading-relaxed">
                            • {en ? "Children (2–9) stay meal-free but count towards bed capacity." : "Çocuklar (2–9 yaş) yemek ücretinden muaftır, yatak hesabına dahildir."}
                        </div>
                        <div className="text-[11px] text-emerald_deep/60 leading-relaxed">
                            • {en ? "Infants (under 2) stay free — no bed, no meal." : "Bebekler (2 yaş altı) yataksız ve yemeksiz konaklar — ücretsizdir."}
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-end px-5 py-3 border-t border-emerald_deep/10">
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="px-5 py-2 text-sm font-bold uppercase tracking-wider text-white hover:brightness-95 transition-all"
                        style={{ background: "var(--c-gold)" }}
                        data-testid={`${testId}-done`}
                    >
                        {en ? "Done" : "Tamam"}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default GuestSelector;
